import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, ListFilter, X } from "lucide-react";
import { ComponentPropsWithoutRef, forwardRef } from "react";
import { ForecastTimePeriod, useStateOfSalesV2Forecast } from "./useForecastData";

const periodLabels: Record<Exclude<ForecastTimePeriod, "this_month" | "next_month">, string> = {
  prev_quarter: "Last quarter",
  this_quarter: "This quarter",
  next_quarter: "Next quarter",
  next_6_months: "Next 6 months",
  this_year: "This year",
};

export const stateOfSalesForecastPeriodOptions = Object.entries(periodLabels).map(
  ([value, label]) => ({ label, value: value as ForecastTimePeriod }),
);

type ForecastModel = ReturnType<typeof useStateOfSalesV2Forecast>;
type FilterOption = { label: string; value: string };

export type StateOfSalesFilterModel<TPeriod extends string> = {
  ownersForSelect: FilterOption[];
  period: TPeriod;
  pipelinesForSelect: FilterOption[];
  selectedOwners: string[];
  selectedPipelines: string[];
  selectedTeams: string[];
  setPeriod: (value: TPeriod) => void;
  setSelectedOwners: (value: string[]) => void;
  setSelectedPipelines: (value: string[]) => void;
  setSelectedTeams: (value: string[]) => void;
  teamsForSelect: FilterOption[];
};

export type StateOfSalesPeriodOption<TPeriod extends string> = {
  label: string;
  value: TPeriod;
};

type StateOfSalesSelectionModel = Pick<
  StateOfSalesFilterModel<string>,
  | "ownersForSelect"
  | "pipelinesForSelect"
  | "selectedOwners"
  | "selectedPipelines"
  | "selectedTeams"
  | "setSelectedOwners"
  | "setSelectedPipelines"
  | "setSelectedTeams"
  | "teamsForSelect"
>;

export function ForecastFilters({ model }: { model: ForecastModel }) {
  return (
    <StateOfSalesFilters
      model={model}
      periodOptions={stateOfSalesForecastPeriodOptions}
    />
  );
}

/** The same period control is shared with Target Outlook. */
export function StateOfSalesPeriodSelector<TPeriod extends string>({ period, onChange, periodOptions }: {
  period: TPeriod;
  onChange: (value: TPeriod) => void;
  periodOptions: StateOfSalesPeriodOption<TPeriod>[];
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Period</span>
      <Select value={period} onValueChange={(value) => onChange(value as TPeriod)}>
        <SelectTrigger aria-label="Period" className="h-8 w-36 bg-white text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {periodOptions.map(({ value, label }) => (
            <SelectItem key={value} value={value}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function StateOfSalesFilters<TPeriod extends string>({
  model,
  periodOptions,
}: {
  model: StateOfSalesFilterModel<TPeriod>;
  periodOptions: StateOfSalesPeriodOption<TPeriod>[];
}) {
  const activeSelections = [
    ...getActiveSelections(
      "Pipeline",
      model.selectedPipelines,
      model.pipelinesForSelect,
      model.setSelectedPipelines,
    ),
    ...getActiveSelections(
      "Team",
      model.selectedTeams,
      model.teamsForSelect,
      model.setSelectedTeams,
    ),
    ...getActiveSelections(
      "Sales rep",
      model.selectedOwners,
      model.ownersForSelect,
      model.setSelectedOwners,
    ),
  ];
  const clearAll = () => {
    model.setSelectedPipelines([]);
    model.setSelectedTeams([]);
    model.setSelectedOwners([]);
  };

  return (
    <div className="flex max-w-full flex-col items-end gap-2">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <StateOfSalesPeriodSelector period={model.period} onChange={model.setPeriod} periodOptions={periodOptions} />

        <div className="hidden sm:block">
          <Popover>
            <PopoverTrigger asChild>
              <FiltersButton count={activeSelections.length} />
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-4">
              <FiltersPanel
                model={model}
                onClearAll={clearAll}
                hasActiveFilters={activeSelections.length > 0}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="sm:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <FiltersButton count={activeSelections.length} />
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm">
              <SheetHeader className="text-left">
                <SheetTitle>Filters</SheetTitle>
                <SheetDescription>
                  These filters follow the State of Sales workflow.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6">
                <FiltersPanel
                  model={model}
                  onClearAll={clearAll}
                  hasActiveFilters={activeSelections.length > 0}
                  showHeading={false}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {activeSelections.length > 0 && (
        <div className="flex max-w-full flex-wrap justify-end gap-1.5">
          {activeSelections.map((selection) => (
            <button
              key={`${selection.dimension}-${selection.value}`}
              type="button"
              onClick={selection.remove}
              className="inline-flex h-7 items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 text-[11px] text-gray-600 hover:border-[#9F86C0] hover:text-[#231942]"
            >
              <span className="max-w-40 truncate">
                {selection.dimension}: {selection.label}
              </span>
              <X className="h-3 w-3" />
            </button>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="h-7 px-1.5 text-[11px] text-gray-500 hover:text-[#231942]"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

const FiltersButton = forwardRef<
  HTMLButtonElement,
  { count: number } & ComponentPropsWithoutRef<typeof Button>
>(({ count, ...props }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 bg-white px-3 text-xs"
      {...props}
    >
      <ListFilter className="h-3.5 w-3.5" />
      {count > 0 ? `Filters · ${count}` : "Filters"}
    </Button>
));

FiltersButton.displayName = "FiltersButton";

function FiltersPanel({
  model,
  hasActiveFilters,
  onClearAll,
  showHeading = true,
}: {
  model: StateOfSalesSelectionModel;
  hasActiveFilters: boolean;
  onClearAll: () => void;
  showHeading?: boolean;
}) {
  return (
    <div className="space-y-4">
      {(showHeading || hasActiveFilters) && (
        <div className="flex items-center justify-between gap-4">
          {showHeading && <p className="text-sm font-medium text-[#231942]">Filters</p>}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs text-gray-500 hover:text-[#231942]"
          >
            Clear all
          </button>
        )}
        </div>
      )}
      <FilterMultiSelect
        label="Pipeline"
        allLabel="All pipelines"
        options={model.pipelinesForSelect}
        value={model.selectedPipelines}
        onValueChange={model.setSelectedPipelines}
      />
      <FilterMultiSelect
        label="Team"
        allLabel="All teams"
        options={model.teamsForSelect}
        value={model.selectedTeams}
        onValueChange={model.setSelectedTeams}
      />
      <FilterMultiSelect
        label="Sales rep"
        allLabel="All sales reps"
        options={model.ownersForSelect}
        value={model.selectedOwners}
        onValueChange={model.setSelectedOwners}
      />
    </div>
  );
}

function FilterMultiSelect({
  allLabel,
  label,
  onValueChange,
  options,
  value,
}: {
  allLabel: string;
  label: string;
  onValueChange: (value: string[]) => void;
  options: FilterOption[];
  value: string[];
}) {
  const selectedLabels = value
    .map((selectedValue) => options.find((option) => option.value === selectedValue)?.label)
    .filter(Boolean) as string[];
  const triggerLabel = value.length === 0
    ? allLabel
    : selectedLabels.length === 0
      ? `${value.length} selected`
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels.length} selected`;

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-gray-500">{label}</p>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="h-9 w-full justify-between bg-white px-3 text-xs font-normal"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>No matching options.</CommandEmpty>
              <CommandGroup>
                <CommandItem onSelect={() => onValueChange([])}>
                  <Check className={cn("mr-2 h-4 w-4", value.length ? "opacity-0" : "opacity-100")} />
                  {allLabel}
                </CommandItem>
                {options.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => onValueChange(
                        isSelected
                          ? value.filter((selectedValue) => selectedValue !== option.value)
                          : [...value, option.value],
                      )}
                    >
                      <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function getActiveSelections(
  dimension: string,
  values: string[],
  options: FilterOption[],
  onValueChange: (values: string[]) => void,
) {
  return values.map((value) => ({
    dimension,
    label: options.find((option) => option.value === value)?.label ?? `${values.length} selected`,
    remove: () => onValueChange(values.filter((selectedValue) => selectedValue !== value)),
    value,
  }));
}
